import {Component, OnInit, ViewChild, ElementRef} from '@angular/core';
import { trigger, state, transition, animate, style } from '@angular/animations';
import { MatDialog, MatTabGroup } from '@angular/material';
import { LocalDataSource } from 'ng2-smart-table';
import * as Highcharts from 'highcharts';
import html2canvas from 'html2canvas';

import { CustomAutoTextEditorComponent } from './custom-auto-text-editor/custom-auto-text-editor.component';
import { CustomAmountEditorComponent } from './custom-amount-editor/custom-amount-editor.component';
import { CustomInterestRateEditorComponent } from './custom-interest-rate-editor/custom-interest-rate-editor.component';
import { CustomButtonEditorComponent } from './custom-button-editor/custom-button-editor.component';
import { ValidationDialogComponent } from './validation-dialog/validation-dialog.component';
import { CustomYearEditorComponent } from './custom-year-editor/custom-year-editor.component';


@Component({
  selector: 'app-loan-table',
  templateUrl: './loan-table.component.html',
  styleUrls: ['./loan-table.component.css'],
  animations: [
    trigger('EnterLeaveUp', [
      state('flyIn', style({ transform: 'translateY(0)' })),
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('0.5s 50ms ease-in')
      ]),
    ]),
    trigger('EnterLeaveFade', [
      state('fadeIn', style({ opacity: '1' })),
      transition(':enter', [
        style({ opacity: '0' }),
        animate('0.5s 50ms ease-in')
      ]),
    ]),
  ]
})

export class LoanTableComponent implements OnInit {

  @ViewChild(MatTabGroup, { static: false }) tabGroup: MatTabGroup;

  Highcharts: typeof Highcharts = Highcharts;
  source: LocalDataSource;

  colours = [
    {
      main: '#2DAD85',
      compliment: '#2a916b'
    },
    {
      main: '#EC7449',
      compliment: '#d86d48'
    },
    {
      main: '#7360A4',
      compliment: '#645291'
    },
    {
      main: '#2585A8',
      compliment: '#246d8f'
    },
    {
      main: '#567EBF',
      compliment: '#536caa'
    },
    {
      main: '#2DAD85',
      compliment: '#2a916b'
    },
    {
      main: '#EC7449',
      compliment: '#d86d48'
    },
    {
      main: '#7360A4',
      compliment: '#645291'
    },
    {
      main: '#2585A8',
      compliment: '#246d8f'
    },
    {
      main: '#567EBF',
      compliment: '#536caa'
    },
    {
      main: '#2DAD85',
      compliment: '#2a916b'
    },
    {
      main: '#EC7449',
      compliment: '#d86d48'
    },
    {
      main: '#7360A4',
      compliment: '#645291'
    },
    {
      main: '#2585A8',
      compliment: '#246d8f'
    },
    {
      main: '#567EBF',
      compliment: '#536caa'
    },
  ];  // TODO: Improve This!

  debtInputTableSettings = {
    add: {
      addButtonContent: 'Add Debt Entry',
      createButtonContent: 'Calculate',
      confirmCreate: true,
    },
    edit: {
      saveButtonContent: 'Recalculate',
      confirmSave: true,
    },

    actions: {
      position: 'right',
      delete: true,
    },

    noDataMessage: '',
    columns: {
      description: {
        title: 'Description',
        filter: false,
        editor: {
          type: 'custom',
          component: CustomAutoTextEditorComponent,
        },
      },
      lender: {
        title: 'Lender',
        filter: false,
        editor: {
          type: 'custom',
          component: CustomAutoTextEditorComponent,
        },
      },
      amount: {
        title: 'Current Loan Amount',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
        editor: {
          type: 'custom',
          component: CustomAmountEditorComponent,
        },
      },
      loanTerm: {
        title: 'Loan Term',
        filter: false,
        valuePrepareFunction: (input: number) => input ? input + ' Years' : 'Auto Calculated',
        editor: {
          type: 'custom',
          component: CustomYearEditorComponent,
        },
      },
      repaymentAmount: {
        title: 'Repayment Amount',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
        editor: {
          type: 'custom',
          component: CustomAmountEditorComponent,
        },
      },
      repaymentFrequency: {
        title: 'Repayment Frequency',
        filter: false,
        editor: {
          type: 'custom',
          component: CustomButtonEditorComponent,
        },
      },
      rate: {
        title: 'Interest Rate',
        filter: false,
        valuePrepareFunction: this.formatAsPercentage,
        editor: {
          type: 'custom',
          component: CustomInterestRateEditorComponent,
        },
      },
      type: {
        title: 'Loan Type',
        filter: false,
        editor: {
          type: 'custom',
          component: CustomButtonEditorComponent,
        },
      },
      offset: {
        title: 'Offset Amount',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
        editor: {
          type: 'custom',
          component: CustomAmountEditorComponent,
        },
      },
    }
  };

  debtSummaryTableSettings = {
    actions: {
      add: false,
      edit: false,
      delete: false,
    },
    hideSubHeader: true,
    noDataMessage: '',
    columns: {
      description: {
        title: 'Description',
        filter: false,
      },
      totalAmount: {
        title: 'Total Amount',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
      },
      totalPayments : {
        title: 'Total Payments',
        filter: false,
      },
      totalInterest: {
        title: 'Total Interest',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
      },
      estimatedDate: {
        title: 'Estimated Payout Date',
        filter: false,
      },
      numberOfYears: {
        title: 'Number Of Years',
        filter: false,
      },
    }
  };

  debtAmortisationTableSettings = {
    actions: {
      width: '36px',
      add: false,
      edit: false,
      delete: false,
      custom: [
        {
          name: 'lumpSumPayment',
          title: 'Add Lump Sum',
        },
      ],
      position: 'right'
    },
    hideSubHeader: true,
    noDataMessage: '',
    columns: {
      Year: {
        title: 'Year',
        filter: false,
      },
      Month : {
        title: 'Month',
        filter: false,
        valuePrepareFunction: this.getMonthNameByNumber,
      },
      Balance: {
        title: 'Balance',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
      },
      Interest: {
        title: 'Interest',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
      },
      Principal: {
        title: 'Principal',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
      },
      Repayment: {
        title: 'Repayment',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
      },
      Additional: {
        title: 'Lump Sum',
        filter: false,
        valuePrepareFunction: this.formatAsCurrency,
      },
    }
  };

  sliderValue = 0;
  totalNumberOfLoans = 0;
  totalInterestAcrossLoans = 0;
  yearsUntilDebtFree = 0;

  debtEntries = [];

  formatAsPercentage(input: number) {
    return input + '%';
  }

  formatAsCurrency(input: number) {
    return '$' + input.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  getMonthNameByNumber(numOfMonth: number) {

    const MONTHS = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    return MONTHS[numOfMonth];

  }

  addMonthsToToday(num) {
    const today = new Date();
    const currentMonth = today.getMonth() + today.getFullYear() * 12;
    today.setMonth(today.getMonth() + num);
    const diff = today.getMonth() + today.getFullYear() * 12 - currentMonth;

    if (diff !== num) {
      today.setDate(0);
    }
    return today;
  }

  getAdditionalPaymentAsMonthlyAmount(userEnteredValue, selectedPaymentFrequency) {

    switch (selectedPaymentFrequency) {
      case 'week':
        return userEnteredValue * 4;
      case 'fortnight':
        return userEnteredValue * 2;
    }

    return userEnteredValue;
  }

  openValidationDialog(validationData) {
    this.validationDialog.open(ValidationDialogComponent, {
      data: validationData
    });
  }

  validateRow(event) {

    const validationErrors = [];
    const validationMessages = {
      description: 'You must give the debt a description.',
      lender: 'You must enter or select a lender.',
      amount : 'You must enter a current loan amount.',
      repaymentAmount : 'You must enter a repayment amount.',
      rate: 'You must enter the interest rate.',
    };

    // Bit dodgy but was a quick last minute addition to calculate the repayment amount automatically from a loan term.
    if (event.newData.loanTerm > 0) {
      delete validationMessages.repaymentAmount;
      event.newData.repaymentAmount = this.calculateMonthlyPaymentFromTerm(
        event.newData.amount,
        event.newData.rate,
        event.newData.loanTerm);
      event.newData.repaymentFrequency = 'Monthly';
    }

    Object.keys(validationMessages).forEach((item) => {
      if (event.newData[item] === '') {
        validationErrors.push(validationMessages[item]);
      }
    });

    if (!validationErrors.length) {

      let payment = 0;

      // TODO: Make a function that does number by frequency
      switch (event.newData.repaymentFrequency) {
        case 'Monthly':
          payment = event.newData.repaymentAmount;
          break;
        case 'Fortnightly':
          payment = (event.newData.repaymentAmount * 26) / 12;
          break;
        case 'Weekly':
          payment = (event.newData.repaymentAmount * 52) / 12;
          break;
      }

      // If no other validation errors exist, check the entered values produce a valid output.
      const numberOfRepayments = this.calculateNumberOfRepayments(event.newData.amount,
        event.newData.rate,
        payment);

      if (isFinite(numberOfRepayments) === false) {

        // The entered details do not produce a valid output. Reject and present the validation prompt.
        event.confirm.reject();
        validationErrors.push('Your repayments are too low to cover the interest');
        this.openValidationDialog(validationErrors);

      } else {

        // All validations passed. Save and calculate.
        event.newData.totalAmount = 0;
        event.newData.totalPayments = 0;
        event.newData.totalInterest = 0;
        event.newData.estimatedDate = 0;
        event.newData.numberOfYears = 0;

        event.confirm.resolve(event.newData);

        this.calculateRow(event, '', '2');
        this.calculateRow(event, '3', '4');

      }
    } else {

      // There are missing required fields. Reject and display the validation prompts.
      event.confirm.reject();
      this.openValidationDialog(validationErrors);

    }

  }

  onCreateConfirm(event) {
    this.sliderValue = 0;
    // Set defaults for the comparison functionality
    event.newData.additionalPaymentEnabled = 'no';
    event.newData.additionalPaymentFrequency = 'week';
    event.newData.additionalPaymentAmount = 100;

    this.validateRow(event);

  }

  onSaveConfirm(event) {
    this.sliderValue = 0;
    this.validateRow(event);
  }

  formatLabel(value: number | null) {
    if (!value) {
      return 'Now';
    }

    return value + ' Months from now';
  }

  // Calculate the number of repayments from the loan amount, annual interest rate and monthly repayment amount.
  calculateNumberOfRepayments(principal, annualInterestRate, monthlyPayment) {
    const monthlyInterestRate = this.calculateMonthlyInterestRate(annualInterestRate);
    return (-Math.log(1 - ((principal * monthlyInterestRate) / monthlyPayment)) / Math.log(1 + monthlyInterestRate) + 1);
  }

  // Calculate the repayment amount based on the provided loan term.
  calculateMonthlyPaymentFromTerm(principal, annualInterestRate, term) {
    const numberOfMonthlyPaymentsInTerm = term * 12;
    const monthlyInterestRate = this.calculateMonthlyInterestRate(annualInterestRate);
    const x = Math.pow(1 + monthlyInterestRate, numberOfMonthlyPaymentsInTerm);
    const calculatedMonthlyRepayment = (principal * x * monthlyInterestRate) / (x - 1);
    return Math.round((calculatedMonthlyRepayment * 100) / 100);
  }

  // Calculate the Monthly interest rate from the Annual interest rate.
  calculateMonthlyInterestRate(annualInterestRate) {
    return annualInterestRate / 100 / 12;
  }

  calculateRow(event, output1, output2) {

    let monthlyAdditionalPaymentAmount = 0;

    // TODO : will do this every time regard of if this is the actual one you want as the secondary comparison
    //        so dodgy workaround for now with && output1 !== ''
    if (event.newData.additionalPaymentEnabled && output1 !== '') {
      monthlyAdditionalPaymentAmount = this.getAdditionalPaymentAsMonthlyAmount(
        event.newData.additionalPaymentAmount,
        event.newData.additionalPaymentFrequency
      );
    }

    const offsetAmount = event.newData.offset;
    let principal = event.newData.amount - offsetAmount;
    const monthlyInterestRate = this.calculateMonthlyInterestRate(event.newData.rate);

    let monthlyPayment = 0;

    // Payment Frequency
    switch (event.newData.repaymentFrequency) {
      case 'Monthly':
        monthlyPayment = event.newData.repaymentAmount;
        break;
      case 'Fortnightly':
        monthlyPayment = (event.newData.repaymentAmount * 26) / 12;
        break;
      case 'Weekly':
        monthlyPayment = (event.newData.repaymentAmount * 52) / 12;
        break;
    }

    monthlyPayment = Number(monthlyPayment) + monthlyAdditionalPaymentAmount;
    monthlyPayment = Math.floor(monthlyPayment);

    let payoutMonths = this.calculateNumberOfRepayments(principal, event.newData.rate, monthlyPayment);


    const outputDate = this.addMonthsToToday(payoutMonths);

    // TODO: Check this handles weekly/fortnightly payments

    function calculateInterestPaid(interestRate, principal, paymentAmount, payments) {
      const monthlyRate = (interestRate / 100) / 12;  // 0.065= APR of 6.5% as decimal
      let amount = 0;
      for (let i = 0; i < payments; i++) {
        const interestForMonth = principal * monthlyRate;
        amount += interestForMonth;
        principal -= paymentAmount;
      }
      return amount;
    }

    const interestPaid = calculateInterestPaid(event.newData.rate, principal, monthlyPayment, this.sliderValue);

    let interest = Math.round((monthlyPayment * payoutMonths) - principal);

    payoutMonths = payoutMonths - this.sliderValue;


    event.newData['totalPayments' + output1] = Math.ceil(payoutMonths);
    event.newData['estimatedDate' + output1] = outputDate.toLocaleDateString('en-au', {month: 'long', year: 'numeric'});
    event.newData['totalAmount' + output1] = Math.round((monthlyPayment * payoutMonths));
    event.newData['totalInterest' + output1] = interest - interestPaid;
    event.newData['numberOfYears' + output1] = Math.round(payoutMonths / 12);



    let balance = principal;

    event.newData['amortisation' + output1] = [];

    const arr = [];

    let lastBalance = 0;

    for (let count = 0; count < Math.floor(payoutMonths); count++) {
      let monthlyInterest = 0;
      let monthlyPrincipal = 0;

      const date = new Date();
      date.setMonth(date.getMonth() + count + this.sliderValue);
      const year = date.getFullYear();

      monthlyInterest = balance * monthlyInterestRate;
      monthlyPrincipal = monthlyPayment - monthlyInterest;
      balance = balance - monthlyPrincipal;

      if (arr[year] === undefined) {
        arr[year] = {
          balance: 0,
          interest: 0,
          principal: 0,
        };
      }
      arr[year].balance === 0 ? arr[year].balance = Number(balance.toFixed(0)) : false; // Start of the year
      arr[year].principal += Number(monthlyPrincipal.toFixed(0));
      arr[year].interest += Number(monthlyInterest.toFixed(0));

      let test = {};

      // Where last payment is more than the remaining balance
      if (lastBalance < Number(monthlyPayment) && count !== 0) {
        test = {
          Year: year,
          Month: date.getMonth(),
          Interest: monthlyInterest.toFixed(0),
          Principal: Number(lastBalance) - Number(monthlyInterest.toFixed(0)),
          Balance: 0,
          Repayment: Number(lastBalance)
        };
       } else {
        test = {
          Year: year,
          Month: date.getMonth(),
          Interest: monthlyInterest.toFixed(0),
          Principal: monthlyPrincipal.toFixed(0),
          Balance: balance.toFixed(0),
          Repayment: Number(monthlyPayment)
        };
      }

      event.newData['amortisation' + output1].push(test);

      lastBalance = Number(balance.toFixed(0));
    }

    const year2 = [];

    Object.keys(arr).forEach((item) => {
      year2.push(item);
    });

    const interest2 = [];
    const balance2 = [];
    const principal2 = [];

    arr.forEach( (item) => {
      interest2.push(item.interest);
      balance2.push(item.balance);
      principal2.push(item.principal);
    });

    const totArr = [];
    totArr.push(event.newData['totalInterest' + output1]);

    const totArr2 = [];
    totArr2.push(Number(event.newData.amount));

    // Interest Vs Principal
    event.newData['chartOptions' + output1] = {
      credits: {
        enabled: false
      },
      title: {
        text: ''
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        x: 0,
        verticalAlign: 'top',
        y: 0,
        floating: false,
      },
      xAxis: [{
        categories: ['Total Loan Amount'],
        crosshair: true
      }],
      yAxis: [{ // Primary yAxis
        title: {
          text: ''
        },
        labels: {
          format: '${value}',
          style: {
            color: '#3a3f48'
          }
        },
      },{ // Secondary yAxis
        title: {
          text: '',
          style: {
            color: '#3a3f48'
          }
        },
        labels: {
          format: '${value}',
          style: {
            color: '#3a3f48'
          }
        },
      }],
      exporting: {
        enabled: false, // true,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          }
        }
      },
      tooltip: {
        headerFormat: '<b>Principal vs Interest</b><br/>',
        pointFormat: '{series.name}: {point.y}<br/>Total: ${point.stackTotal}'
      },
      series: [
        {
          name: 'Interest',
          type: 'column',
          yAxis: 1,
          data: totArr,
          tooltip: {
            valuePrefix: '$'
          },
          color: 'rgb(236,116,73)',

        },
        {
          name: 'Principle',
          type: 'column',
          yAxis: 1,
          data: totArr2,
          tooltip: {
            valuePrefix: '$'
          },
          color: '#2585A8',
        }]
    };

    // Breakdown of Payments, Principal and Interest
    event.newData['chartOptions' + output2] = {
      chart: {
      },
      credits: {
        enabled: false
      },
      title: {
        text: ''
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        x: 0,
        verticalAlign: 'top',
        y: 0,
        floating: false,
      },
      xAxis: [{
        categories: year2,
        crosshair: true
      }],
      yAxis: [{ // Secondary yAxis
        min: 0,
        title: {
          text: 'Balance',
          style: {
            color: '#3a3f48'
          }
        },
        labels: {
          format: '${value}',
          style: {
            color: '#3a3f48'
          }
        },
        opposite: true
      },
        { // Primary yAxis
          title: {
            text: 'Annual Interest and Principle Repayments'
          },
          style: {
            color: '#3a3f48'
          },
          labels: {
            format: '${value}',
            style: {
              color: '#3a3f48'
            }
          },

        }],
      exporting: {
        enabled: false, // true,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          }
        }
      },
      tooltip: {
        headerFormat: '<b>{point.x}</b><br/>',
        pointFormat: '{series.name}: {point.y}<br/>Total: ${point.stackTotal}'
      },
      series: [
        {
          name: 'Interest',
          type: 'column',
          yAxis: 1,
          data: interest2,
          tooltip: {
            valuePrefix: '$'
          },
          color: 'rgb(236,116,73)',

        },
        {
          name: 'Principle',
          type: 'column',
          yAxis: 1,
          data: principal2,
          tooltip: {
            valuePrefix: '$'
          },
          color: '#2585A8',


        },  {
          name: 'Balance',
          type: 'spline',
          data: balance2,
          tooltip: {
            valuePrefix: '$'
          },
          color: '#3a3f48',

        }]
    };


    // // Dodgy workaround to reload chart on recalculate
    if (event.hasOwnProperty('data')) {
      setTimeout(() => {
        Highcharts.chart('chart' + output1, event.newData['chartOptions' + output1]);
        Highcharts.chart('chart' + output2, event.newData['chartOptions' + output2]);
      });
    }
  }

  calculateTotals() {
    let totalInterest = 0;
    let highestYear = 0;

    this.totalInterestAcrossLoans = 0;
    this.yearsUntilDebtFree = 0;

    this.source.getAll().then((items) => {
      if (items.length !== 0) {
        items.forEach((item) => {
          totalInterest += item.totalInterest;
          this.totalInterestAcrossLoans = totalInterest;
          highestYear = item.numberOfYears > highestYear ? item.numberOfYears : highestYear;
          this.yearsUntilDebtFree = highestYear;
        });
      }
    });

  }

  calculateAdditionalPayment(currentId) {
    this.source.getAll().then((element) => {
      this.calculateRow( { newData: element[currentId] }, '3', '4');
    });
  }



  exportHTMLElementAsImage(event) {

    // Navigate two levels up the DOM tree to obtain the correct container element.
    const parentElement = event.target.parentElement.parentElement;

    // Hide the calling button
    event.target.style.display = 'none';

    // Get the current scroll position
    const x = window.pageXOffset || document.documentElement.scrollLeft;
    const y = window.pageYOffset || document.documentElement.scrollTop;

    // Set scroll to top of screen to fix render bug with html2canvas
    window.scrollTo(0, 0);

    // Provide html2canvas with the element to convert into an image
    html2canvas(parentElement).then(canvas => {

      // Restore the calling button
      event.target.style.display = 'inline-block';

      // Restore the scroll position to the original location
      window.scrollTo(x, y);

      // Convert the image to a Base64 string
      const contentType = 'image/png';
      const base64ImageData = canvas.toDataURL(contentType);

      // Launch a new browser tab and populate it with the base64 image.
      const newWindow = window.open('', '_blank');
      newWindow.document.write(`
        <head>
            <style>

              img {
                -webkit-user-select: none;
                color:white;
              }

              body {
                  margin: 0;
                  background: #0e0e0e;
                  text-align: center;
                  position: absolute;
                  top: 50%;
                  left:50%;
                  -webkit-transform: translate(-50%, -50%);
                  transform: translate(-50%, -50%;
              }
            </style>
            <title>Press CTRL + C</title>
        </head>
        <body>
            <img alt=" - Unable to render image" src="${ base64ImageData }" />
        </body>
        <script>
            document.oncopy = logCopy;
            function logCopy(event) {
                event.preventDefault();
                window.close();
            }
        </script>
        `);
    });
  }

  // TODO: Allow input of Array of elements to hide)
  exportHTMLElementAsImageByID(event, element, transparentBackground = false, ) {

    // Navigate two levels up the DOM tree to obtain the correct container element.
    // const parentElement = event.target.parentElement.parentElement;

    const elementToCapture = document.getElementById(element);

    // Get original display value of the elements parent
    const originalDisplayValue = elementToCapture.parentElement.style.display;

    // Set parent display value to inline block to remove as much whitespace as possible.
    elementToCapture.parentElement.style.display = 'inline-block';

    // Hide the calling button
    event.target.style.display = 'none';

    // Get the current scroll position
    const x = window.pageXOffset || document.documentElement.scrollLeft;
    const y = window.pageYOffset || document.documentElement.scrollTop;

    // Set scroll to top of screen to fix render bug with html2canvas
    window.scrollTo(0, 0);

    const options = transparentBackground ? { backgroundColor: 'null' } : {};

    // Provide html2canvas with the element to convert into an image
    html2canvas(elementToCapture, options).then(canvas => {

      // Restore the calling button
      event.target.style.display = 'inline-block';

      // Restore the scroll position to the original location
      window.scrollTo(x, y);

      // Restore the original display value of the elements parent
      elementToCapture.parentElement.style.display = originalDisplayValue;

      // Convert the image to a Base64 string
      const contentType = 'image/png';
      const base64ImageData = canvas.toDataURL(contentType);

      // Launch a new browser tab and populate it with the base64 image.
      const newWindow = window.open('', '_blank');
      newWindow.document.write(`
        <head>
            <style>

              img {
                -webkit-user-select: none;
                color:white;
              }

              body {
                  margin: 0;
                  background: #0e0e0e;
                  text-align: center;
                  position: absolute;
                  top: 50%;
                  left:50%;
                  -webkit-transform: translate(-50%, -50%);
                  transform: translate(-50%, -50%;
              }
            </style>
            <title>Press CTRL + C</title>
        </head>
        <body>
            <img alt=" - Unable to render image" src="${ base64ImageData }" />
        </body>
        <script>
            document.oncopy = logCopy;
            function logCopy(event) {
                event.preventDefault();
                window.close();
            }
        </script>
        `);
    });
  }


  scrollToBottom() {

    // Give angular 5ms to show the contents so that the scrollHeight is correctly detected

    setTimeout(() => {
      window.scrollTo(0, document.body.scrollHeight);
    }, 5);

  }

  test() {

    this.debtEntries.map((data) => {
      const temp = { newData: data};
      this.calculateRow(temp, '', '2');
      this.calculateRow(temp, '3', '4');
    });

    this.calculateTotals();

  }

  constructor(public validationDialog: MatDialog) {}

  ngOnInit() {
    this.source = new LocalDataSource(this.debtEntries);

    this.source.onChanged().subscribe(error => {

      // Update the debtEnteries used by the graphs and summary sections
      this.source.getAll().then(data => this.debtEntries = data);

      // Recalculate Total Values
      this.calculateTotals();
      this.totalNumberOfLoans = this.source.count();

      // Switch to first tab
      setTimeout(() => {
        if (this.tabGroup !== undefined) {
          this.tabGroup.selectedIndex = 0;
        }
      });
    });
  }

}
